import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Segmented,
  Space,
  Table,
  Typography,
  message as toast,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined } from '@ant-design/icons';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { colors } from 'theme/tokens';
import UserPicker, { UserPickerValue } from 'pages/star-shared/components/user-picker';
import { DEPARTMENT_LABEL } from 'pages/star-shared/labels';
import { useFeed } from './shared/context';
import { PersonAvatar, Pill } from './shared/components';
import { FeedMemberRow, FeedPageEditorRow, PersonRef } from './shared/types';
import { feedError, fmtDate, fmtDateTime, personText, useFeedList, useUrlFilters } from './shared/utils';
import styles from './styles.module.scss';

const PAGE_SIZE = 50;
const BULK_MAX = 500;

type MemberView = 'candidates' | 'active' | 'revoked';

const PersonCell = ({ person }: { person?: PersonRef | null }) => (
  <div style={{ display: 'flex', gap: 8, alignItems: 'center', minWidth: 0 }}>
    <PersonAvatar name={person?.name} avatar={person?.avatar} size={32} />
    <div style={{ minWidth: 0 }}>
      <Typography.Text ellipsis style={{ fontWeight: 600, display: 'block', maxWidth: 170 }}>
        {personText(person)}
      </Typography.Text>
      {person?.username && <span className={styles.muted}>@{person.username}</span>}
    </div>
  </div>
);

interface BulkState {
  kind: 'grant' | 'revoke';
  rows: FeedMemberRow[];
}

/** Hộp xác nhận hàng loạt: liệt kê tên (tối đa 20, còn lại "+n") + ô ghi chú. */
const BulkModal = ({ state, onClose, onDone }: { state: BulkState | null; onClose: () => void; onDone: () => void }) => {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => setNote(''), [state]);
  if (!state) return null;
  const grant = state.kind === 'grant';
  const names = state.rows.slice(0, 20).map((r) => personText(r.user));
  const rest = state.rows.length - names.length;

  const submit = async () => {
    setSaving(true);
    try {
      const userIds = state.rows.map((r) => r.user.userId).slice(0, BULK_MAX);
      const body = note.trim() ? { userIds, note: note.trim() } : { userIds };
      const res: any = await AxiosUtils.post(grant ? API.feedAdminMembers : API.feedAdminMembersRevoke, body);
      if (grant) {
        const granted = res?.data?.granted ?? userIds.length;
        const skipped = res?.data?.skipped ?? 0;
        toast.success(`Đã xác nhận ${granted} người${skipped ? ` (${skipped} bỏ qua)` : ''}.`);
      } else {
        toast.success(`Đã thu hồi ${res?.data?.revoked ?? userIds.length} người.`);
      }
      onClose();
      onDone();
    } catch (err) {
      toast.error(feedError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      width={560}
      title={grant ? `Xác nhận ${state.rows.length} người` : `Thu hồi ${state.rows.length} người`}
      onCancel={onClose}
      onOk={submit}
      okText={grant ? 'Xác nhận' : 'Thu hồi'}
      okButtonProps={{ danger: !grant }}
      cancelText="Huỷ"
      confirmLoading={saving}
      destroyOnHidden
    >
      <p style={{ marginBottom: 8 }}>
        {grant
          ? 'Những người này sẽ vào được Bảng tin (lớp Nhân viên) và thấy thẻ Chấm công, Điểm Sao ở Trang chủ app:'
          : 'Những người này mất quyền vào Bảng tin ngay ở lần mở app kế tiếp:'}
      </p>
      <div className={styles.box} style={{ maxHeight: 200, overflow: 'auto', marginBottom: 12 }}>
        {names.join(', ')}
        {rest > 0 ? ` +${rest}` : ''}
      </div>
      {state.rows.length > BULK_MAX && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`Mỗi lần tối đa ${BULK_MAX} người — chỉ ${BULK_MAX} người đầu được gửi.`}
        />
      )}
      <Form layout="vertical">
        <Form.Item label="Ghi chú (không bắt buộc)">
          <Input.TextArea value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} autoSize={{ minRows: 2, maxRows: 4 }} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// ------------------------------------------------------------------ phần 1: thành viên nội bộ (D31–D33)

const MembersSection = () => {
  const { refreshKey, isAdmin, summary, afterAction } = useFeed();
  const { get, set, clear } = useUrlFilters();
  const view = (get('mstatus') as MemberView) || 'candidates';
  const page = Number(get('page')) || 1;
  const [search, setSearch] = useState(get('q') || '');
  const [selected, setSelected] = useState<FeedMemberRow[]>([]);
  const [bulk, setBulk] = useState<BulkState | null>(null);
  const { items, total, loading } = useFeedList<FeedMemberRow>(
    API.feedAdminMembers,
    { status: view, q: get('q'), page, limit: PAGE_SIZE },
    { refreshKey }
  );

  useEffect(() => setSelected([]), [view, refreshKey]);

  const selectable = isAdmin && view !== 'revoked';

  const columns: ColumnsType<FeedMemberRow> = [
    { title: 'Người', key: 'user', width: 220, render: (_, r) => <PersonCell person={r.user} /> },
    {
      title: 'Phòng ban (tự khai)',
      key: 'department',
      width: 150,
      render: (_, r) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
          <span>{r.department ? DEPARTMENT_LABEL[r.department] || r.department : '—'}</span>
          {r.isHpCompany && (
            <Pill color={colors.infoInk} bg={colors.infoSoft}>
              Nhân viên HP
            </Pill>
          )}
        </div>
      ),
    },
    { title: 'Đăng ký lúc', key: 'registeredAt', width: 124, render: (_, r) => fmtDate(r.registeredAt) },
    {
      title: view === 'revoked' ? 'Thu hồi bởi / lúc' : 'Xác nhận bởi / lúc',
      key: 'by',
      width: 180,
      render: (_, r) => {
        const by = view === 'revoked' ? r.revokedBy : r.grantedBy;
        const at = view === 'revoked' ? r.revokedAt : r.grantedAt;
        if (!by && !at) return <span style={{ color: colors.textMuted }}>—</span>;
        return (
          <div style={{ fontSize: 13 }}>
            {personText(by)}
            <div className={styles.muted}>{fmtDateTime(at)}</div>
            {r.note && <div className={styles.muted}>“{r.note}”</div>}
          </div>
        );
      },
    },
  ];
  if (isAdmin) {
    columns.push({
      title: 'Thao tác',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, r) =>
        view === 'active' ? (
          <Button size="small" danger onClick={() => setBulk({ kind: 'revoke', rows: [r] })}>
            Thu hồi
          </Button>
        ) : (
          <Button size="small" type="primary" onClick={() => setBulk({ kind: 'grant', rows: [r] })}>
            Xác nhận
          </Button>
        ),
    });
  }

  return (
    <div style={{ marginBottom: 32 }}>
      <h3 className={styles.sectionTitle}>Thành viên nội bộ (Nhân viên, Creator NPC có vai trò User)</h3>
      <div className={styles.hint} style={{ marginBottom: 12 }}>
        Nhân viên Admin đã xác nhận ở trang <strong>Xác nhận nhân viên</strong> tự là lớp Nhân viên của Bảng tin —
        không cần cấp ở đây và không nằm trong danh sách này. Ở đây chỉ cấp riêng cho người khác (vd Creator NPC có
        vai trò User). Tự chọn "Phòng ban" trong Hồ sơ hoặc lúc đăng ký thì <strong>không</strong> được tính. Người
        chưa được xác nhận sẽ thấy "Không có quyền truy cập" ở Bảng tin và không thấy thẻ Chấm công, Điểm Sao ở
        Trang chủ app. Thu hồi có hiệu lực ngay. Nhân viên nghỉ việc: gỡ ở trang Xác nhận nhân viên hoặc khoá tài
        khoản.
      </div>
      <div className={styles.toolbar}>
        <div className={styles.filters} style={{ marginBottom: 0 }}>
          <Segmented
            value={view}
            options={[
              {
                value: 'candidates',
                label: `Ứng viên${summary?.memberCandidates ? ` (${summary.memberCandidates})` : ''}`,
              },
              { value: 'active', label: 'Đã xác nhận' },
              { value: 'revoked', label: 'Đã thu hồi' },
            ]}
            onChange={(v) => clear(['tab', 'post'], v !== 'candidates' ? { mstatus: String(v) } : undefined)}
          />
          <Input.Search
            allowClear
            placeholder="Tên hoặc username"
            style={{ width: 240 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onSearch={(v) => set({ q: v.trim() })}
          />
        </div>
        {selectable && (
          <Space wrap>
            {view === 'candidates' && items.length > 0 && (
              <Button onClick={() => setSelected(items.slice(0, BULK_MAX))}>Chọn tất cả ứng viên trang này</Button>
            )}
            {selected.length > 0 &&
              (view === 'candidates' ? (
                <Button type="primary" onClick={() => setBulk({ kind: 'grant', rows: selected })}>
                  Xác nhận {selected.length} người
                </Button>
              ) : (
                <Button danger onClick={() => setBulk({ kind: 'revoke', rows: selected })}>
                  Thu hồi {selected.length} người
                </Button>
              ))}
          </Space>
        )}
      </div>
      {view === 'candidates' && (
        <div className={styles.hint} style={{ marginBottom: 8 }}>
          Ứng viên = tài khoản vai trò User đã tự khai phòng ban hoặc "thuộc công ty", chưa được xác nhận. Nhân viên
          thật nên xác nhận ở trang Xác nhận nhân viên (vào luôn Bảng tin và Điểm Sao). Hãy đối chiếu danh sách nhân
          sự thật — fan cũng có thể tự khai. Không có nút "xác nhận tất cả".
        </div>
      )}
      <div className="custom-table">
        <Table<FeedMemberRow>
          rowKey={(r) => String(r.user?.userId)}
          size="middle"
          loading={loading}
          columns={columns}
          dataSource={items}
          locale={{
            emptyText:
              view === 'candidates'
                ? 'Không có ứng viên nào chờ xác nhận.'
                : view === 'active'
                ? 'Chưa xác nhận ai.'
                : 'Chưa thu hồi ai.',
          }}
          scroll={items.length ? { x: 842 } : undefined}
          rowSelection={
            selectable
              ? {
                  columnWidth: 48,
                  selectedRowKeys: selected.map((r) => String(r.user?.userId)),
                  onChange: (_, rows) => setSelected(rows.filter(Boolean).slice(0, BULK_MAX)),
                  preserveSelectedRowKeys: true,
                }
              : undefined
          }
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total,
            showSizeChanger: false,
            hideOnSinglePage: true,
            onChange: (p) => set({ page: p }),
          }}
        />
      </div>
      <BulkModal state={bulk} onClose={() => setBulk(null)} onDone={afterAction} />
    </div>
  );
};

// ------------------------------------------------------------------ phần 2: Biên tập trang HP Media (D34–D36)

const PageEditorsSection = () => {
  const { refreshKey, isAdmin, afterAction } = useFeed();
  const { items, loading } = useFeedList<FeedPageEditorRow>(API.feedAdminPageEditors, {}, { refreshKey });
  const [adding, setAdding] = useState(false);
  const [user, setUser] = useState<UserPickerValue>();
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const add = async () => {
    if (!user?.value) return;
    setSaving(true);
    try {
      const res: any = await AxiosUtils.post(
        API.feedAdminPageEditors,
        note.trim() ? { userId: user.value, note: note.trim() } : { userId: user.value }
      );
      const name = res?.data?.user?.name || String(user.label || '').split(' — ')[0] || `#${user.value}`;
      toast.success(`Đã thêm ${name} vào Biên tập trang.`);
      setAdding(false);
      setUser(undefined);
      setNote('');
      afterAction();
    } catch (err) {
      // FEED_PAGE_EDITOR_INVALID: "Chỉ thêm được tài khoản MOD vào Biên tập trang."
      toast.error(feedError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row: FeedPageEditorRow) => {
    try {
      await AxiosUtils._delete(`${API.feedAdminPageEditors}/${row.user.userId}`, undefined);
      toast.success(`Đã bỏ ${personText(row.user)} khỏi Biên tập trang.`);
      afterAction();
    } catch (err) {
      toast.error(feedError(err));
    }
  };

  const columns: ColumnsType<FeedPageEditorRow> = [
    { title: 'Người', key: 'user', width: 220, render: (_, r) => <PersonCell person={r.user} /> },
    { title: 'Thêm bởi', key: 'addedBy', width: 160, render: (_, r) => personText(r.addedBy) },
    { title: 'Lúc', key: 'addedAt', width: 144, render: (_, r) => fmtDateTime(r.addedAt) },
    {
      title: 'Ghi chú',
      key: 'note',
      render: (_, r) => <span style={{ color: r.note ? undefined : colors.textMuted }}>{r.note || '—'}</span>,
    },
  ];
  if (isAdmin) {
    columns.push({
      title: 'Thao tác',
      key: 'actions',
      width: 96,
      fixed: 'right',
      render: (_, r) => (
        <Popconfirm
          title={`Bỏ ${personText(r.user)} khỏi Biên tập trang?`}
          okText="Bỏ"
          cancelText="Huỷ"
          onConfirm={() => remove(r)}
        >
          <Button size="small" danger>
            Bỏ
          </Button>
        </Popconfirm>
      ),
    });
  }

  return (
    <div>
      <div className={styles.toolbar}>
        <h3 className={styles.sectionTitle} style={{ margin: 0 }}>
          Biên tập trang HP Media
        </h3>
        {isAdmin && (
          <Button icon={<PlusOutlined />} onClick={() => setAdding(true)}>
            Thêm người
          </Button>
        )}
      </div>
      <div className={styles.hint} style={{ marginBottom: 12 }}>
        Biên tập trang được đăng bài tên HP Media, đánh dấu Quan trọng và được miễn giữ bài. Không sửa/xoá được bài
        HP Media do Admin đăng. Chỉ thêm được tài khoản MOD — phòng ban "QL Vận Hành" không tự cho quyền này.
      </div>
      <div className="custom-table">
        <Table<FeedPageEditorRow>
          rowKey={(r) => String(r.user?.userId)}
          size="middle"
          loading={loading}
          columns={columns}
          dataSource={items}
          pagination={false}
          locale={{ emptyText: 'Chưa có Biên tập trang nào (chỉ Admin đăng được tên HP Media).' }}
          scroll={items.length ? { x: 760 } : undefined}
        />
      </div>
      <Modal
        open={adding}
        width={560}
        title="Thêm Biên tập trang HP Media"
        onCancel={() => setAdding(false)}
        onOk={add}
        okText="Thêm"
        cancelText="Huỷ"
        okButtonProps={{ disabled: !user?.value }}
        confirmLoading={saving}
        destroyOnHidden
      >
        <Form layout="vertical">
          <Form.Item label="Tài khoản MOD" required extra="Người không có vai trò MOD sẽ bị từ chối.">
            <UserPicker size="middle" value={user} onChange={setUser} />
          </Form.Item>
          <Form.Item label="Ghi chú (không bắt buộc)">
            <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

/** Chỉ tổng follower thực của UID TikTok đã nối; file LIVE Backstage chỉ có "Follower mới". */
const TiktokFollowersSection = () => {
  const { isAdmin } = useFeed();
  const [user, setUser] = useState<UserPickerValue>();
  const [count, setCount] = useState<number | null>(null);
  const [observedAt, setObservedAt] = useState('');
  const [sourceNote, setSourceNote] = useState('');
  const [current, setCurrent] = useState<string>('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    setCurrent('');
    setCount(null);
    if (!user?.value) return;
    AxiosUtils.get(`/feed/users/${user.value}/social`)
      .then((res: any) => {
        if (!active) return;
        const tiktok = res?.data?.tiktok;
        if (!tiktok) setCurrent('Tài khoản chưa có UID TikTok đã liên kết.');
        else if (tiktok.followers === null) setCurrent('Đã liên kết UID, chưa nhập tổng follower.');
        else setCurrent(`Đang lưu: ${Number(tiktok.followers).toLocaleString('vi-VN')} follower · ${tiktok.observedAt?.slice(0, 10) || '—'}`);
      })
      .catch(() => { if (active) setCurrent('Không đọc được thông tin Creator.'); });
    return () => { active = false; };
  }, [user?.value]);

  const save = async () => {
    if (!user?.value || count === null || !observedAt) return;
    setSaving(true);
    try {
      await AxiosUtils.put(`${API.feedAdminTiktokFollowers}/${user.value}`, {
        followers: count,
        observedAt,
        sourceNote: sourceNote.trim() || undefined,
      });
      setCurrent(`Đang lưu: ${count.toLocaleString('vi-VN')} follower · ${observedAt}`);
      toast.success('Đã cập nhật tổng follower TikTok.');
    } catch (err) {
      toast.error(feedError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ marginTop: 32 }}>
      <h3 className={styles.sectionTitle}>Follow TikTok (số liệu đối chiếu)</h3>
      <div className={styles.hint} style={{ marginBottom: 12 }}>
        Chỉ nhập <strong>tổng follower</strong> từ số liệu/ảnh TikTok có ngày đo. File LIVE Backstage hiện chỉ có
        “Follower mới” theo kỳ, không thể dùng làm tổng. App nối theo UID TikTok đã liên kết; không có số thì hiện —.
      </div>
      {isAdmin && (
        <Form layout="inline" style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
          <Form.Item label="Creator"><UserPicker size="middle" value={user} onChange={setUser} /></Form.Item>
          <Form.Item label="Tổng follower"><InputNumber min={0} max={2147483647} value={count} onChange={setCount} style={{ width: 135 }} /></Form.Item>
          <Form.Item label="Ngày đo"><Input type="date" max={new Date().toISOString().slice(0, 10)} value={observedAt} onChange={(e) => setObservedAt(e.target.value)} /></Form.Item>
          <Form.Item label="Nguồn"><Input placeholder="VD: ảnh hồ sơ TikTok" value={sourceNote} onChange={(e) => setSourceNote(e.target.value)} maxLength={255} /></Form.Item>
          <Button type="primary" loading={saving} disabled={!user?.value || count === null || !observedAt} onClick={save}>Lưu</Button>
        </Form>
      )}
      {current && <p className={styles.muted} style={{ marginTop: 12 }}>{current}</p>}
    </div>
  );
};

/** Tab "Thành viên" (03 §10A). MOD chỉ xem; cấp/thu hồi, thêm/bỏ Biên tập trang chỉ ADMIN. */
const MembersTab = () => {
  const { isAdmin } = useFeed();
  return (
    <div>
      {!isAdmin && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Chỉ Admin xác nhận/thu hồi thành viên và Biên tập trang. Bạn đang xem ở chế độ chỉ đọc."
        />
      )}
      <MembersSection />
      <PageEditorsSection />
      <TiktokFollowersSection />
    </div>
  );
};

export default MembersTab;
